import React, { useEffect, useRef, useState } from 'react'
//LIBRARIES
import Select from 'react-select'
import { useForm, Controller } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClose, faCalculator } from '@fortawesome/free-solid-svg-icons'
//COMPONENTS
import Loading from '../../components/Loading'
import { useRequest } from '../../hooks/UseRequest'
import { getPayrollUI } from '../../components/PayrollUI'
//REDUX
import { selectModal, setModal } from '../../redux/modalSlice'
import { setNotification } from '../../redux/notificationSlice'
import { selectDropdowns, selectDropdownOptions } from '../../redux/dropdownSlice'
//HELPERS
import { DateFormat, CurrencyFormat, formatApr } from '../../helpers/formatting'
import { reactSelectClassNames } from '../../helpers/react-select-styles'
import { handleErrorResponse, generateErrorBody } from '../../helpers/errorHandling'
import { frequencyOptions, payOnHolidayOptions } from '../../helpers/loanCalcHelper'

export default function LoanCalculatorModal() {
  const [loaded, setLoaded] = useState(false)
  const requests = useRequest()
  const dispatch = useDispatch()
  const requesting = useRef(false)
  const dropdowns = useSelector(selectDropdowns)
  const dropdownOptions = useSelector(selectDropdownOptions)

  const modal = useSelector(selectModal)
  const { title } = modal

  const [responseData, setResponseData] = useState(null);
  const [selectedFrequencyValue, setSelectedFrequencyValue] = useState("");
  const [selectedHolidayValue, setSelectedHolidayValue] = useState("");
  const [selectedPayrollValue, setSelectedPayrollValue] = useState("");
  const payFrequency = dropdowns.data.filter((o) => o.code_key === 'pay_frequency')[0]
  const payFrequencyOptions = dropdownOptions.filter((o) => o.dropdown_id === payFrequency.id && o.code_key != 'pay_frequency_daily').map((o) => ({ label: o.name, value: o.code_key }))

  const getResponse = async () => {
    setLoaded(true)
    requesting.current = false
  }

  useEffect(() => {
    if (requesting.current === false) {
      setLoaded(false)
      requesting.current = true
      getResponse()
    }
  })

  const slideUpDrawer = () => {
    dispatch(setModal({ title: `Loan Calculator`, content: ``, type: '', is_active: false }))
  }

  const [saving, setSaving] = useState(false)

  const {
    register, handleSubmit, control, setValue, setError, formState: { errors }, watch, reset, clearErrors } = useForm();

  const customerId = watch("customer_id", "");
  const frequency = watch("frequency", "");
  const pay_on_holiday = watch("pay_on_holiday", "");
  const payroll_info_semi_monthly = watch("payroll_info_semi_monthly", "");
  const payroll_info_monthly = watch("payroll_info_monthly", "");
  const isCustomerIdNumeric = () => !Number.isNaN(customerId) && customerId.trim() !== '';

  const payrollUI = getPayrollUI(frequency, payroll_info_semi_monthly, payroll_info_monthly, register, isCustomerIdNumeric, Select, reactSelectClassNames, setValue, clearErrors, selectedPayrollValue, setSelectedPayrollValue);

  const dispatchErorr = () => {
    if (Object.keys(errors).length > 0) {
      const body = generateErrorBody(errors, [])
      dispatch(setNotification({ type: 'error', body: body }))
    }
  }

  const beforeFormSubmit = (event) => {
    event.preventDefault();
    dispatchErorr();
    clearErrors('payroll_info');
    clearErrors('payment_schedule');
    handleSubmit(onSubmit)();
  };

  const onSubmit = async (formData) => {
    clearErrors('payroll_info');
    clearErrors('payment_schedule');
    setSaving(true)
    let formError = false

    if (formData['loan_amount'] === '') {
      setError("loan_amount", { type: "manual", message: "Loan Amount is required" });
      formError = true
    }

    if (formData['apr'] === '') {
      setError("apr", { type: "manual", message: "APR is required" });
      formError = true
    }

    if (formData['number_of_payments'] === '') {
      setError("number_of_payments", { type: "manual", message: "Number of Payments is required" });
      formError = true
    }

    if (formData['origination_date'] === '') {
      setError("origination_date", { type: "manual", message: "Origination Date is required" });
      formError = true
    }

    let payroll_info = '';
    if (formData['frequency'] === 'pay_frequency_weekly') {
      if (formData['payDayOfWeek'] === null) {
        setError("payroll_info", { type: "manual", message: "Please select pay day of the week" });
        formError = true
      } else {
        payroll_info = { payDayOfWeek: formData['payDayOfWeek'] };
      }
    } else if (formData['frequency'] === 'pay_frequency_biweekly') {
      if (formData['firstPayDay'] === "") {
        setError("payroll_info", { type: "manual", message: "Please select first pay day" });
        formError = true
      } else {
        payroll_info = { firstPayDay: formData['firstPayDay'] };
      }
    } else if (formData['frequency'] === 'pay_frequency_semimonthly') {
      if (payroll_info_semi_monthly === 'specificDays') {
        if (formData['specificDays'].length !== 2) {
          setError("payroll_info", { type: "manual", message: "Please select 2 days" });
          formError = true
        } else {
          payroll_info = { specificDays: formData['specificDays'] }
        }
      } else if (payroll_info_semi_monthly == "weekdayAndWeekOfMonth") {
        if (formData['dayOfWeek1'] === undefined || formData['dayOfWeek1'] === null ||
          formData['weekOfMonth1'] === undefined || formData['weekOfMonth1'] === null ||
          formData['dayOfWeek2'] === undefined || formData['dayOfWeek2'] === null ||
          formData['weekOfMonth2'] === undefined || formData['weekOfMonth2'] === null) {
          setError("payroll_info", { type: "manual", message: "The payroll info is invalid" });
          formError = true;
        } else if (formData['weekOfMonth1'] === formData['weekOfMonth2']) {
          setError("payroll_info", { type: "manual", message: "Can't have 2 payments in the same week!" });
          formError = true;
        } else {
          payroll_info = {
            weekdayAndWeekOfMonth: [
              {
                dayOfWeek: formData['dayOfWeek1'],
                weekOfMonth: formData['weekOfMonth1']
              },
              {
                dayOfWeek: formData['dayOfWeek2'],
                weekOfMonth: formData['weekOfMonth2']
              }
            ]
          };
        }
      }
    } else if (formData['frequency'] === 'pay_frequency_monthly') {
      if (payroll_info_monthly === 'payDayOfMonth') {
        if (formData['payDayOfMonth'] === "") {
          setError("payroll_info", { type: "manual", message: "Please enter pay day of the month" });
          formError = true
        } else {
          payroll_info = { payDayOfMonth: formData['payDayOfMonth'] }
        }
      } else if (formData['dayOfWeek'] !== undefined && formData['dayOfWeek'] !== null && formData['weekOfMonth'] !== "") {
        payroll_info = { weekdayAndWeekOfMonth: { dayOfWeek: formData['dayOfWeek'], weekOfMonth: formData['weekOfMonth'] } };
      } else {
        setError("payroll_info", { type: "manual", message: "The payroll info is invalid" });
        formError = true
      }
    } else {
      payroll_info = '';
    }

    if (formData['customer_id'] === '' && frequency === '') {
      setError("frequency", {
        type: "manual",
        message: "Field is required when customer id is not present"
      });
      formError = true
    }

    if (formData['customer_id'] === '' && pay_on_holiday === '') {
      setError("pay_on_holiday", {
        type: "manual",
        message: "Field is required when customer id is not present"
      });
      formError = true
    }

    if (formError == true) {
      setSaving(false)
      dispatchErorr()
      return
    }

    const sanitizedData = formData['customer_id'] === ''
      ? {
        loan_amount: formData['loan_amount'],
        number_of_payments: formData['number_of_payments'],
        origination_date: formData['origination_date'],
        frequency: frequency,
        pay_on_holiday: pay_on_holiday,
        payroll_info: payroll_info,
        type: 'loan_calculator',
        apr: formData['apr']
      }
      : {
        loan_amount: formData['loan_amount'],
        number_of_payments: formData['number_of_payments'],
        origination_date: formData['origination_date'],
        customer_id: formData['customer_id'],
        type: 'loan_calculator',
        apr: formData['apr']
      };

    requests.setEndpoint(
      `${process.env.REACT_APP_API_DOMAIN}/loan_calculator`
    )

    try {
      const response = await requests.post(sanitizedData)
      if (response.status === 200 || response.status === 201) {
        dispatch(setNotification({ type: 'success', body: '' }))
        setSaving(false)
        setResponseData(response.data)
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        setSaving(false)
      }
    } catch (error) {
      setSaving(false)
      handleErrorResponse(error.response?.data?.errors, setError, false)
      // dispatch(setNotification({ type: 'error', body: 'There was an error submitting' }))
    }
  };

  useEffect(() => {
    dispatchErorr()
  }, [errors, saving])

  const onCancel = () => {
    reset();
    setResponseData(null)
    reset({ frequency: "", pay_on_holiday: "" })
    setSelectedFrequencyValue("")
    setSelectedHolidayValue("")
  };

  return modal.is_active && modal.type === 'calculator' ? (
    <div
      className="modal is-active is-large"
    >
      <div className="modal-background" onClick={slideUpDrawer}></div>
      <div className="modal-card column is-four-fifths">
        <header className="modal-card-head py-3">
          <p className="modal-card-title is-capitalized">
            <FontAwesomeIcon icon={faCalculator} className="is-size-5 pt-1" />{' '}
            &nbsp;{title}
          </p>
          <button
            className="delete"
            aria-label="close"
            onClick={slideUpDrawer}
          ></button>
        </header>
        <section
          className="modal-card-body modal-card-body pb-1"
          style={{ maxHeight: '900px', minHeight: '450px', overflow: 'hidden' }}
        >
          {!loaded && (
            <div className="is-flex is-justify-content-center is-align-items-center" style={{ height: '295px' }}>
              <Loading loaded={loaded} />
            </div>
          )}

          <div className="columns">
            <div className="column">
              {loaded && (
                <form onSubmit={beforeFormSubmit} className="form lox-form px-1 py-1">
                  <div className="columns mb-0">
                    <div className="column">
                      <p className="has-text-grey is-small is-size-7">
                        <span className="mr-1">Loan Amount $</span>
                        <span className="has-text-danger">*</span>
                      </p>
                      <input
                        id="loan_amount"
                        name="loan_amount"
                        className="input is-small"
                        type="number"
                        step="0.01"
                        placeholder="Loan Amount $"
                        {...register('loan_amount')}
                      />
                      <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                        {errors.loan_amount && <span>{errors.loan_amount.message ? errors.loan_amount.message : "Loan Amount is required"}</span>}
                      </p>
                    </div>

                    <div className="column">
                      <div className="is-block">
                        <p className="has-text-grey is-small is-size-7">
                          <span className="mr-1">APR %</span>
                          <span className="has-text-danger">*</span>
                        </p>
                        <Controller
                          name="apr"
                          control={control}
                          render={({ field }) => (
                            <input
                              id="apr"
                              name="apr"
                              className="input is-small"
                              type="number"
                              step="0.001"
                              placeholder="Apr %"
                              {...field}
                            />
                          )}
                        />
                        <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                          {errors.apr && <span>{errors.apr.message ? errors.apr.message : "APR is required"}</span>}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="columns mb-0">
                    <div className="column">
                      <p className="has-text-grey is-small is-size-7">
                        <span className="mr-1">Number of Installments/Payments</span>
                        <span className="has-text-danger">*</span>
                      </p>
                      <input
                        name="number_of_payments"
                        className="input is-small"
                        type="number"
                        placeholder="Number of Installments/Payments"
                        {...register('number_of_payments')}
                      />
                      <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                        {errors.number_of_payments && <span>{errors.number_of_payments.message ? errors.number_of_payments.message : "Number of Payments is required"}</span>}
                      </p>
                    </div>

                    <div className="column">
                      <p className="has-text-grey is-small is-size-7">
                        <span className="mr-1">Origination Date</span>
                        <span className="has-text-danger">*</span>
                      </p>
                      <input
                        name="origination_date"
                        className="input is-small"
                        type="date"
                        placeholder="Origination Date"
                        {...register('origination_date')}
                      />
                      <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                        {errors.origination_date && <span>{errors.origination_date.message ? errors.origination_date.message : "Origination Date is required"}</span>}
                      </p>
                    </div>
                  </div>

                  <div className="columns mb-0">
                    <div className="column">
                      <p className="has-text-grey is-small is-size-7">
                        <span className="mr-1">Customer ID</span>
                        <span className="is-hidden">*</span>
                      </p>
                      <input
                        name="customer_id"
                        className="input is-small"
                        type="number"
                        placeholder="Customer ID"
                        {...register('customer_id', { required: false })}
                      />
                      <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                        {errors.customer_id && <span>{errors.customer_id.message}</span>}
                      </p>
                    </div>

                    <div className="column">
                      <p className="has-text-grey is-small is-size-7">
                        <span className="mr-1">Frequency</span>
                        <span className="has-text-danger">*</span>
                      </p>
                      <div>
                        <Controller
                          name="frequency"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              classNames={reactSelectClassNames()}
                              options={payFrequencyOptions}
                              isDisabled={isCustomerIdNumeric()} // Disable if Customer ID has a numeric value
                              onChange={(e) => {
                                setValue('frequency', e.value);
                                if (e.value != "") {
                                  clearErrors('frequency')
                                  clearErrors('payroll_info')
                                  setValue("payroll_info_semi_monthly", "");
                                  setValue("payroll_info_monthly", "");
                                  setSelectedPayrollValue("");
                                }
                                setSelectedFrequencyValue(e);
                              }}
                              value={selectedFrequencyValue}
                              isSearchable={false}
                            />
                          )}
                        />
                      </div>
                      <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                        {errors.frequency && <span>{errors.frequency.message}</span>}
                      </p>
                    </div>
                  </div>

                  <div className="columns mb-0">
                    <div className="column">
                      <p className="has-text-grey is-small is-size-7">
                        <span className="mr-1">Pay on Holiday</span>
                        <span className="has-text-danger">*</span>
                      </p>
                      <div>
                        <Controller
                          name="pay_on_holiday"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              classNames={reactSelectClassNames()}
                              options={payOnHolidayOptions}
                              isDisabled={isCustomerIdNumeric()} // Disable if Customer ID has a numeric value
                              onChange={(e) => {
                                setValue('pay_on_holiday', e.value);
                                if (e.value != "") {
                                  clearErrors('pay_on_holiday')
                                  clearErrors('payroll_info')
                                }
                                setSelectedHolidayValue(e);
                              }}
                              value={selectedHolidayValue}
                              isSearchable={false}
                            />
                          )}
                        />
                      </div>
                      <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                        {errors.pay_on_holiday && <span>{errors.pay_on_holiday.message}</span>}
                      </p>
                    </div>

                    <div className="column">
                      <div className={`${frequency === '' || isCustomerIdNumeric() ? 'is-hidden' : ''}`}>
                        <p className="has-text-grey is-small is-size-7 has-text-weight-bold">
                          <span className="mr-1">Payroll Info {frequency}</span>
                        </p>
                        <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                          {errors.payroll_info && <span>{errors.payroll_info.message}</span>}
                        </p>
                        <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                          {errors.payment_schedule && <span>{errors.payment_schedule.message}</span>}
                        </p>
                        {payrollUI}
                      </div>
                    </div>
                  </div>

                  <div className="columns mb-0">
                    <div className="column is-half" />
                    <div className="column">
                      <div className="columns">
                        <div className="column is-half">
                          <button
                            type="button"
                            className="button is-small is-outlined is-fullwidth"
                            onClick={onCancel}
                          >
                            Clear
                          </button>
                        </div>
                        <div className="column is-half">
                          <button
                            type="submit"
                            className="button is-link is-small is-fullwidth"
                            disabled={saving && 'disabled'}
                          >
                            {saving ? (
                              <>
                                Calculating
                              </>
                            ) : 'Calculate'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </div>
            <div className="column" style={{ maxHeight: "700px", overflow: "auto" }}>
              {responseData && (
                <>
                  <div className="columns is-multiline">
                    <div className="column is-half">
                      <p><strong>Total Amount:</strong> <CurrencyFormat value={responseData.total_amount} /></p>
                      <p><strong>Total Interest:</strong> <CurrencyFormat value={responseData.total_interest} /></p>
                      <p><strong>Loan Due Date:</strong> <DateFormat dateString={responseData.loan_due_date} /></p>
                    </div>
                    <div className="column is-half">
                      <p><strong>Effective Date:</strong> <DateFormat dateString={responseData.effective_date} /></p>
                      <p><strong>APR:</strong> {formatApr(responseData?.apr)}%</p>
                      <p><strong>Interest Rate:</strong> {parseFloat(responseData?.interest_rate).toFixed(2)}%</p>
                    </div>
                  </div>
                  <div className="table-container">
                    <table className="lox-hooktable-table table is-bordered is-striped is-narrow is-hoverable is-fullwidth is-grey mb-3">
                      <thead>
                        <tr>
                          <th colSpan="4" className="is-selected has-text-left lox-payment-schedule-color">Payment Schedule</th>
                        </tr>
                        <tr>
                          <th className="is-selected has-text-centered lox-payment-schedule-color">
                            #
                          </th>
                          <th className="is-selected has-text-centered lox-payment-schedule-color">
                            Date
                          </th>
                          <th className="is-selected has-text-centered lox-payment-schedule-color">
                            Principal Amount
                          </th>
                          <th className="is-selected has-text-centered lox-payment-schedule-color">
                            Payment
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {responseData.schedule.map((item, index) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <tr key={index}>
                            <td className="has-text-centered" style={{ whiteSpace: 'nowrap' }}>{index + 1}</td>
                            <td
                              className="has-text-left"
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              <DateFormat dateString={item.date} />
                            </td>
                            <td
                              className="has-text-left"
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              <CurrencyFormat value={item.principal_amount} />
                            </td>
                            <td
                              className="has-text-left"
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              <CurrencyFormat value={item.payment} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <thead>
                        <tr>
                          <th colSpan="2" className="is-selected has-text-centered" style={{ backgroundColor: "#7F7F7F" }}>TOTAL PLAN</th>
                          <th className="is-selected has-text-centered" style={{ backgroundColor: "#7F7F7F" }}>
                            <CurrencyFormat value={responseData.schedule.reduce((total, item) => total + item.principal_amount, 0)} />
                          </th>
                          <th className="is-selected has-text-centered" style={{ backgroundColor: "#7F7F7F" }}><CurrencyFormat value={responseData.total_amount} /></th>
                        </tr>
                      </thead>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
        <footer className="modal-card-foot is-flex is-justify-content-flex-end py-2">
          <a
            className="lox-history-close-button has-background-grey has-text-white is-medium px-2"
            onClick={slideUpDrawer}
          >
            <FontAwesomeIcon icon={faClose} /> Close
          </a>
        </footer>
      </div>
    </div>
  ) : null;
}
